/**
 * El marco de enfoque, medido desde el recorte real (004 · US1 · T096; FR-002).
 *
 * El marco de `FocusFrame` tenía un tamaño propio (`min(72vw, 320px)`) que no venía de ningún
 * sitio. Mientras el recorte también era fijo (80 % del lado menor del fotograma) la mentira era
 * pequeña y constante; con el recorte adaptativo de T096 sería una guía que **miente**: el
 * trabajador encuadraría el código en un marco que no corresponde a lo que se está leyendo.
 *
 * Aquí se hace la única conversión que hay entre los dos mundos: el ROI está en píxeles de cámara
 * y el marco en píxeles CSS, y entre medias está el `object-fit: cover` del `<video>`. La
 * aritmética vive en `roiOverlaySide` (`src/camera/roi.ts`), junto al recorte, para que nadie
 * pueda cambiar uno sin ver el otro.
 *
 * El viewport se lee de `window`: la pantalla de cámara es `fixed inset-0` y el `<video>` la cubre
 * entera, así que el cuadro de presentación **es** el viewport. Se reevalúa al rotar o
 * redimensionar (la barra de direcciones de los navegadores móviles cuenta como redimensionar).
 */
import { useEffect, useState } from 'react';

import { roiOverlaySide, type ScanRoi } from '../../camera';

export interface Viewport {
  width: number;
  height: number;
}

function currentViewport(): Viewport {
  if (typeof window === 'undefined') {
    return { width: 0, height: 0 };
  }
  return { width: window.innerWidth, height: window.innerHeight };
}

/**
 * Lado del marco de enfoque en píxeles CSS, o `null` mientras no haya recorte que dibujar (la
 * cámara todavía no entregó fotograma). En ese caso `FocusFrame` usa su tamaño de reserva, que es
 * honesto porque **tampoco se está recortando nada** todavía.
 */
export function useRoiOverlaySide(roi: ScanRoi | null): number | null {
  const [viewport, setViewport] = useState<Viewport>(currentViewport);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const update = (): void => {
      setViewport((previous) => {
        const next = currentViewport();
        return previous.width === next.width && previous.height === next.height ? previous : next;
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return roiOverlaySide(roi, viewport.width, viewport.height);
}
